import config from '../config/environment';
import supabase from '../config/supabase';
import {
  parsePerformanceSupportDashboard,
  type PerformanceSupportDashboard,
  type PerformanceSupportDashboardQuery,
  type PerformanceSupportResponse,
} from '../utils/performanceSupportDashboard';
import { handleServiceError } from '../utils/errorHandler';

class PerformanceSupportDashboardServiceClass {
  async getDashboard(query: PerformanceSupportDashboardQuery): Promise<PerformanceSupportDashboard> {
    const token = await this.getAccessToken();
    const url = this.buildUrl(query);

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: this.buildHeaders(token),
    });
    const body = await response.json().catch(() => ({})) as { error?: string };

    if (!response.ok) {
      handleServiceError(
        new Error(body.error || 'Erro ao carregar o suporte da performance'),
        'carregar o suporte da performance',
      );
    }

    const snapshot = parsePerformanceSupportDashboard(body);
    if (!snapshot) {
      handleServiceError(
        new Error('Erro ao carregar o suporte da performance'),
        'carregar o suporte da performance',
      );
    }

    return snapshot;
  }

  async markResponded(
    supportId: number,
    responseChoice: PerformanceSupportResponse,
  ): Promise<{ respondedAt: string | null; response: PerformanceSupportResponse | null }> {
    const token = await this.getAccessToken();
    const httpResponse = await fetch(`${config.supabaseFunctionsUrl}/performance-support-dashboard`, {
      method: 'POST',
      headers: this.buildHeaders(token),
      body: JSON.stringify({ supportId, response: responseChoice }),
    });
    const body = await httpResponse.json().catch(() => ({})) as {
      error?: string;
      respondedAt?: unknown;
      response?: unknown;
    };

    if (!httpResponse.ok) {
      handleServiceError(
        new Error(body.error || 'Erro ao registrar a resposta'),
        'registrar a resposta',
      );
    }

    const savedResponse = body.response === 'yes' || body.response === 'no' || body.response === 'none'
      ? body.response
      : null;

    return {
      respondedAt: typeof body.respondedAt === 'string' && body.respondedAt.trim()
        ? body.respondedAt
        : null,
      response: savedResponse,
    };
  }

  private buildUrl(query: PerformanceSupportDashboardQuery): URL {
    const url = new URL(`${config.supabaseFunctionsUrl}/performance-support-dashboard`);
    url.searchParams.set('page', String(query.page));
    url.searchParams.set('pageSize', String(query.pageSize));

    const search = query.searchName.trim();
    if (search) {
      url.searchParams.set('search', search);
    }

    for (const reason of query.reasons) {
      url.searchParams.append('reason', reason);
    }

    return url;
  }

  private async getAccessToken(): Promise<string | undefined> {
    const { data: sessionData } = await supabase.auth.getSession();
    return sessionData?.session?.access_token;
  }

  private buildHeaders(token: string | undefined): HeadersInit {
    return {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      apikey: config.supabaseAnonKey,
    };
  }
}

export const PerformanceSupportDashboardService = new PerformanceSupportDashboardServiceClass();
